import { useState, useEffect } from 'react';
import { Loader2, Trash2, Clock, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';

interface ProjectsListProps {
  onSelectProject: (project: any) => void;
}

type FilterType = 'all' | 'drafts' | 'published';

export function ProjectsList({ onSelectProject }: ProjectsListProps) {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await api.getProjects();
      setProjects(data.projects);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      setDeleting(projectId);
      await api.deleteProject(projectId);
      setProjects(projects.filter(p => p.id !== projectId));
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const filteredProjects = projects.filter((project) => {
    if (filter === 'drafts') return !project.is_exported;
    if (filter === 'published') return project.is_exported;
    return true;
  });

  const filters: { value: FilterType; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'drafts', label: 'Drafts' },
    { value: 'published', label: 'Published' },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Loader2 className="animate-spin text-primary-500 mb-4" size={48} />
        <p className="text-gray-500 font-medium">Loading your memes...</p>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-32 animate-fade-in">
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary-100 to-orange-100 mb-6">
          <span className="text-6xl">🎨</span>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">No memes yet</h3>
        <p className="text-gray-500 mb-6">
          Create your first meme to get started!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-primary-600 to-gray-900 bg-clip-text text-transparent">
            My Memes
          </h2>
          <p className="text-gray-600 mt-2">
            {filteredProjects.length} {filteredProjects.length === 1 ? 'meme' : 'memes'}
            {filter !== 'all' && ` • ${filter}`}
          </p>
        </div>

        <div className="flex gap-2 bg-white/80 backdrop-blur-sm p-1.5 rounded-2xl shadow-sm">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`
                px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 whitespace-nowrap
                ${filter === f.value
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30 scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {filteredProjects.map((project, index) => (
          <div
            key={project.id}
            onClick={() => onSelectProject(project)}
            className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer animate-scale-in"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-50 relative overflow-hidden">
              {project.thumbnail_url ? (
                <img
                  src={project.thumbnail_url}
                  alt={project.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-6xl opacity-50">🖼️</span>
                </div>
              )}

              {project.is_exported && (
                <div className="absolute top-3 right-3">
                  <div className="badge badge-success shadow-lg">
                    Published
                  </div>
                </div>
              )}

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            <div className="p-4">
              <h3 className="font-bold text-gray-900 mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors">
                {project.name}
              </h3>

              {project.template_name && (
                <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
                  {project.template_name}
                </p>
              )}

              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Clock size={12} />
                  <span>{formatDate(project.updated_at)}</span>
                </div>

                <div className="flex items-center gap-1">
                  {project.exported_url && (
                    <a
                      href={project.exported_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-all duration-200 hover:scale-110"
                      title="View published meme"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                  <button
                    onClick={(e) => handleDelete(project.id, e)}
                    disabled={deleting === project.id}
                    className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200 hover:scale-110"
                  >
                    {deleting === project.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
